import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { type FieldErrors, useForm } from 'react-hook-form';

import type { ExamItemCreateViewModel } from '@/shared/api/generated';
import {
  requiresModelAnswer,
  supportsKeyTerms,
  supportsSubtype,
} from '@/shared/lib/examItemSubtype';
import { useSession } from '@/shared/store/hooks/useSession';
import type { FileWithProgress } from '@/shared/ui/atoms';

import type { QuestionDetail } from '../../../hooks/useQuestionEdit';
import type { QuestionLanguage } from '../../../hooks/useQuestionLibrary';
import { applyAiReviewToForm } from '../../../utils';

import { type QuestionEditFormValues, questionFormSchema } from './schema';

/**
 * Ordem visual dos campos no formulário (topo → base). Usada para rolar até o
 * primeiro campo inválido ao tentar salvar, respeitando o layout da tela e não
 * a ordem arbitrária do objeto de erros do React Hook Form.
 */
const FIELD_SCROLL_ORDER: readonly string[] = [
  'componentType',
  'subtype',
  'label',
  'information',
  'modelAnswer',
  'keyTerms',
  'min',
  'max',
  'correctAnswer',
  'decimalPlaces',
  'maxAudioTimeInSeconds',
  'maxFileSizeInMegabytes',
  'companyId',
  'disciplineId',
  'weight',
  'level',
  'cultureInfo',
];

/**
 * Rola a página até o primeiro campo com erro de validação e foca o controle
 * interno (input/textarea/editor). O campo é localizado pelo atributo
 * `data-field-error="<nome>"` no seu contêiner.
 */
function scrollToFirstError(errors: FieldErrors<QuestionEditFormValues>) {
  const errorNames = Object.keys(errors);
  if (errorNames.length === 0) return;

  const target = FIELD_SCROLL_ORDER.find(name => errorNames.includes(name)) ?? errorNames[0];

  const anchor = document.querySelector<HTMLElement>(`[data-field-error="${target}"]`);
  if (!anchor) return;

  anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
  anchor
    .querySelector<HTMLElement>('input, textarea, select, [contenteditable="true"], [tabindex]')
    ?.focus({ preventScroll: true });
}

export interface UseQuestionEditFormParams {
  readonly question: QuestionDetail;
  readonly onSave: (data: Partial<QuestionDetail>) => void;
  readonly onPreviewChange?: (data: {
    text: string;
    alternatives: QuestionDetail['alternatives'];
    correctId: string;
  }) => void;
}

/**
 * Encapsula formulário (RHF), estado de UI (alternativas, item, files, dependência)
 * e handlers do QuestionEditForm.
 */
export function useQuestionEditForm({
  question,
  onSave,
  onPreviewChange,
}: UseQuestionEditFormParams) {
  const { session } = useSession();

  const form = useForm<QuestionEditFormValues>({
    resolver: zodResolver(questionFormSchema),
    // O foco automático do RHF depende de refs nativas que os componentes ADS e
    // o editor rich text não expõem; rolamos manualmente via `scrollToFirstError`.
    shouldFocusError: false,
    defaultValues: {
      label: question.text,
      information: question.information,
      weight: question.weight,
      cultureInfo: question.language as string,
      showLabel: question.showLabel,
      showInformation: question.showInformation,
      teacherEvaluation: question.teacherEvaluation,
      randomizeAlternatives: question.randomizeAlternatives,
      required: question.requiredField,
      fixed: question.alwaysInEvaluation,
      pageBreak: question.pageBreak,
      personAuthorId: question.authorId ?? session?.personId ?? 0,
      ...(question.componentType ? { componentType: question.componentType } : {}),
      ...(question.min != null ? { min: question.min } : {}),
      ...(question.max != null ? { max: question.max } : {}),
      ...(question.correctAnswer != null ? { correctAnswer: question.correctAnswer } : {}),
      ...(question.decimalPlaces != null ? { decimalPlaces: question.decimalPlaces } : {}),
      ...(question.maxAudioTimeInSeconds != null
        ? { maxAudioTimeInSeconds: question.maxAudioTimeInSeconds }
        : {}),
      ...(question.maxFileSizeInMegabytes != null
        ? { maxFileSizeInMegabytes: question.maxFileSizeInMegabytes }
        : {}),
      ...(question.level != null ? { level: question.level } : {}),
      ...(question.disciplineId != null ? { disciplineId: question.disciplineId } : {}),
      ...(question.companyId != null ? { companyId: question.companyId } : {}),
      ...(question.subtype != null ? { subtype: question.subtype } : {}),
      ...(question.modelAnswer != null ? { modelAnswer: question.modelAnswer } : {}),
      ...(question.keyTerms != null ? { keyTerms: question.keyTerms } : {}),
    },
  });

  const [alternatives, setAlternatives] = useState(question.alternatives);
  const [correctAlternativeIds, setCorrectAlternativeIds] = useState<string[]>(() =>
    question.alternatives.filter(a => a.isCorrect).map(a => a.id),
  );
  const [item, setItem] = useState(question.item);
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  // Marca que o usuário aceitou uma sugestão de revisão com IA; enviado ao salvar.
  const [aiAssisted, setAiAssisted] = useState(false);

  const [depAuthor, setDepAuthor] = useState(() => {
    const initialAuthorId = question.authorId ?? session?.personId;
    return initialAuthorId != null ? String(initialAuthorId) : '';
  });
  const [depDiscipline, setDepDiscipline] = useState('');
  const [depLanguage, setDepLanguage] = useState('');
  const [depParentQuestion, setDepParentQuestion] = useState('');

  const currentLabel = form.watch('label') ?? question.text;
  const watchedComponentType = form.watch('componentType') as number | undefined;
  const componentTypeId = (watchedComponentType ?? question.componentType ?? 1) as number;
  const isChoiceType = componentTypeId === 1 || componentTypeId === 2;
  const isMultipleChoice = componentTypeId === 2;
  const isNumericType = componentTypeId === 5;
  const isGradeType = componentTypeId === 6;
  const isAudioType = componentTypeId === 7;
  const isAttachmentType = componentTypeId === 8;

  // ─── Subtipo dissertativo ──────────────────────────────────────────────────
  // Só o componente MultilineText aceita subtipo; dentro dele, `modelAnswer` é
  // obrigatório para ModelAnswerEssay/MbaCase e `keyTerms` existe apenas para
  // ModelAnswerEssay.
  const watchedSubtype = form.watch('subtype') as number | null | undefined;
  const hasSubtype = supportsSubtype(componentTypeId);
  const subtypeId = hasSubtype ? (watchedSubtype ?? null) : null;
  const hasModelAnswer = hasSubtype && requiresModelAnswer(subtypeId);
  const hasKeyTerms = hasSubtype && supportsKeyTerms(subtypeId);

  function addFiles(newFiles: File[]) {
    setFiles(prev => [...prev, ...newFiles.map(f => ({ file: f, progress: 100 }))]);
  }

  function removeFile(fileName: string) {
    setFiles(prev => prev.filter(f => f.file.name !== fileName));
  }

  function handleAddAlternative() {
    const trimmed = item.replace(/<[^>]*>/g, '').trim();
    if (!trimmed) return;
    const newAlt = { id: String(Date.now()), value: trimmed, isCorrect: false };
    const newAlts = [...alternatives, newAlt];
    setAlternatives(newAlts);
    setItem('');
    onPreviewChange?.({
      text: currentLabel,
      alternatives: newAlts,
      correctId: correctAlternativeIds[0] ?? '',
    });
  }

  function handleDeleteAlternative(id: string) {
    const newAlts = alternatives.filter(a => a.id !== id);
    const newCorrectIds = correctAlternativeIds.filter(cid => cid !== id);
    setAlternatives(newAlts);
    setCorrectAlternativeIds(newCorrectIds);
    onPreviewChange?.({
      text: currentLabel,
      alternatives: newAlts,
      correctId: newCorrectIds[0] ?? '',
    });
  }

  function handleCorrectChange(id: string) {
    const newIds = isMultipleChoice
      ? correctAlternativeIds.includes(id)
        ? correctAlternativeIds.filter(cid => cid !== id)
        : [...correctAlternativeIds, id]
      : [id];
    setCorrectAlternativeIds(newIds);
    onPreviewChange?.({
      text: currentLabel,
      alternatives,
      correctId: newIds[0] ?? '',
    });
  }

  function notifyLabelChange(value: string) {
    onPreviewChange?.({
      text: value,
      alternatives,
      correctId: correctAlternativeIds[0] ?? '',
    });
  }

  /**
   * Aplica a sugestão da IA ao formulário (adoção tudo-ou-nada): substitui
   * enunciado, informação, resposta-modelo/termos-chave (subtipos dissertativos)
   * e, para tipos de escolha, as alternativas. Não salva — a gravação continua
   * sob o botão "Salvar".
   */
  function applyAiReview(result: ExamItemCreateViewModel) {
    setAiAssisted(true);

    const patch = applyAiReviewToForm(result, {
      label: currentLabel,
      information: form.getValues('information') ?? '',
      modelAnswer: (form.getValues('modelAnswer') as string | null | undefined) ?? '',
      keyTerms: (form.getValues('keyTerms') as string | null | undefined) ?? '',
      alternatives,
      correctAlternativeIds,
      isChoiceType,
    });

    form.setValue('label', patch.label);
    form.setValue('information', patch.information);

    if (hasModelAnswer) form.setValue('modelAnswer', patch.modelAnswer);
    if (hasKeyTerms) form.setValue('keyTerms', patch.keyTerms);

    if (isChoiceType) {
      setAlternatives(patch.alternatives);
      setCorrectAlternativeIds(patch.correctAlternativeIds);
    }

    onPreviewChange?.({
      text: patch.label,
      alternatives: isChoiceType ? patch.alternatives : alternatives,
      correctId: patch.correctAlternativeIds[0] ?? '',
    });
  }

  const handleSave = form.handleSubmit(data => {
    onSaveInternal(data);
  }, scrollToFirstError);

  function onSaveInternal(data: QuestionEditFormValues) {
    onSave({
      ...(aiAssisted ? { isAiAssisted: true } : {}),
      componentType: (data.componentType as number) ?? question.componentType,
      text: data.label,
      information: data.information ?? '',
      authorId: data.personAuthorId ?? null,
      disciplineId: data.disciplineId ?? null,
      companyId: data.companyId ?? null,
      weight: data.weight ?? 1,
      level: (data.level as number | null | undefined) ?? null,
      language: (data.cultureInfo ?? 'pt-BR') as QuestionLanguage,
      showLabel: data.showLabel ?? false,
      showInformation: data.showInformation ?? false,
      teacherEvaluation: data.teacherEvaluation ?? false,
      randomizeAlternatives: data.randomizeAlternatives ?? false,
      requiredField: data.required ?? false,
      alwaysInEvaluation: data.fixed ?? false,
      pageBreak: data.pageBreak ?? false,
      item: isChoiceType ? item : '',
      alternatives: isChoiceType
        ? alternatives.map(a => ({
            ...a,
            isCorrect: correctAlternativeIds.includes(a.id),
          }))
        : [],
      ...(isNumericType || isGradeType
        ? {
            min: (data.min as number | null | undefined) ?? null,
            max: (data.max as number | null | undefined) ?? null,
          }
        : {}),
      ...(isNumericType
        ? {
            correctAnswer: (data.correctAnswer as string | null | undefined) ?? null,
            decimalPlaces: (data.decimalPlaces as number | null | undefined) ?? null,
          }
        : {}),
      ...(isAudioType
        ? {
            maxAudioTimeInSeconds:
              (data.maxAudioTimeInSeconds as number | null | undefined) ?? null,
          }
        : {}),
      ...(isAttachmentType
        ? {
            maxFileSizeInMegabytes:
              (data.maxFileSizeInMegabytes as number | null | undefined) ?? null,
          }
        : {}),
      // Enviados sempre (null quando não aplicável) para que a troca de tipo ou
      // de subtipo limpe os valores antigos no backend em vez de mantê-los.
      subtype: subtypeId,
      modelAnswer: hasModelAnswer
        ? ((data.modelAnswer as string | null | undefined) ?? null)
        : null,
      keyTerms: hasKeyTerms ? ((data.keyTerms as string | null | undefined) ?? null) : null,
    });
  }

  return {
    form,
    componentTypeId,
    isChoiceType,
    isMultipleChoice,
    isNumericType,
    isGradeType,
    isAudioType,
    isAttachmentType,
    hasSubtype,
    subtypeId,
    hasModelAnswer,
    hasKeyTerms,
    alternatives,
    correctAlternativeIds,
    item,
    setItem,
    files,
    addFiles,
    removeFile,
    depAuthor,
    setDepAuthor,
    depDiscipline,
    setDepDiscipline,
    depLanguage,
    setDepLanguage,
    depParentQuestion,
    setDepParentQuestion,
    currentLabel,
    handleAddAlternative,
    handleDeleteAlternative,
    handleCorrectChange,
    notifyLabelChange,
    applyAiReview,
    handleSave,
  } as const;
}

export type UseQuestionEditFormReturn = ReturnType<typeof useQuestionEditForm>;
